import { useCallback, useState, type ReactNode } from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

type ConfirmDialogOptions = {
  title: ReactNode
  description: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  destructive?: boolean
  onConfirm: () => void
}

export function useConfirmDialog() {
  const [request, setRequest] = useState<ConfirmDialogOptions | null>(null)

  const confirm = useCallback((options: ConfirmDialogOptions) => {
    setRequest(options)
  }, [])

  const ConfirmDialog = useCallback(() => (
    <AlertDialog
      open={Boolean(request)}
      onOpenChange={(open) => {
        if (!open) {
          setRequest(null)
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{request?.title}</AlertDialogTitle>
          <AlertDialogDescription>{request?.description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{request?.cancelLabel ?? 'Cancel'}</AlertDialogCancel>
          <AlertDialogAction
            className={request?.destructive ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : undefined}
            onClick={(event) => {
              event.preventDefault()
              const onConfirm = request?.onConfirm
              setRequest(null)
              onConfirm?.()
            }}
          >
            {request?.confirmLabel ?? 'Confirm'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  ), [request])

  return { confirm, ConfirmDialog }
}
